import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Plus, RefreshCw, Search } from "lucide-react";
import { get } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { canWritePilotage } from "@/lib/permissions";
import { fmtMin, fmtNum, fmtPct } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiTile } from "@/components/common/KpiTile";
import { Panel } from "@/components/common/Panel";
import { DataTable } from "@/components/common/DataTable";
import { LoadingState, ErrorState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { OfFormDialog } from "@/components/of/OfFormDialog";
import { ofColumns } from "@/components/of/ofColumns";
import { cn } from "@/lib/utils";

const STATUTS = [["", "Tous actifs + jour"], ["prevu", "Prévus"], ["en_production", "En production"], ["pause", "Pause"], ["cloture", "Clôturés"], ["annule", "Annulés"]];

export default function OfPage() {
  const { entrepriseId, user } = useAuth();
  const navigate = useNavigate();
  const [statut, setStatut] = useState("");
  const [ligne, setLigne] = useState("");
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(null); // null | "new" | of

  const q = useQuery({ queryKey: ["of", "pilotage", statut, entrepriseId], queryFn: () => get("/of", statut ? { statut } : {}), refetchInterval: 10000 });
  const kpi = useQuery({ queryKey: ["kpi-planificateur", entrepriseId], queryFn: () => get("/of/kpi-planificateur"), refetchInterval: 10000 });
  const lignes = useQuery({ queryKey: ["lignes"], queryFn: () => get("/lignes") });

  const rows = useMemo(() => (q.data || []).filter((o) =>
    (!ligne || o.ligne_id === Number(ligne)) &&
    (!search || [o.numero, o.produit, o.ref_client, o.equipe, o.superviseur, o.commentaire].join(" ").toLowerCase().includes(search.toLowerCase()))
  ), [q.data, ligne, search]);

  const k = kpi.data;
  const columns = useMemo(() => ofColumns({ onEdit: (of) => setForm(of) }), []);

  return (
    <div data-testid="of-page" className="space-y-3">
      <PageHeader crumbs={[{ label: "ProdTrack" }, { label: "Production" }, { label: "Ordres de fabrication" }]} title="Centre de planification — Ordres de fabrication"
        subtitle="Vue pilotage : OF actifs + OF clôturés/annulés aujourd'hui. L'historique complet est dans Archives OF."
        actions={<>
          <Button size="sm" variant="outline" className="h-8" onClick={() => { q.refetch(); kpi.refetch(); }} data-testid="of-refresh"><RefreshCw className={cn("h-3.5 w-3.5 mr-1", q.isFetching && "animate-spin")} />Actualiser</Button>
          {canWritePilotage(user.role) && <Button size="sm" className="h-8 font-semibold" onClick={() => setForm("new")} data-testid="of-new-button"><Plus className="h-4 w-4 mr-1" />Nouvel OF</Button>}
        </>} />

      {k && (
        <div data-testid="kpi-planificateur" className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
          <KpiTile testId="kpi-of-planifies" label="OF planifiés aujourd'hui" value={k.of_planifies_aujourdhui} sub={`${k.of_annules_aujourdhui} annulé(s)`} />
          <KpiTile testId="kpi-of-en-prod" label="En production" value={k.of_en_production} sub={`${k.lignes_occupees}/${k.nb_lignes} lignes occupées`} />
          <KpiTile testId="kpi-of-retard" label="OF en retard (live)" value={k.of_en_retard} tone={k.of_en_retard > 0 ? "crit" : "ok"} sub={k.retard_moyen_min != null ? `Moyen ${fmtMin(k.retard_moyen_min)} · cumulé ${fmtMin(k.retard_cumule_min)}` : "Aucun retard"} />
          <KpiTile testId="kpi-respect" label="Respect planning" value={fmtPct(k.respect_planning_pct, 0)} tone={k.respect_planning_pct == null ? "none" : k.respect_planning_pct >= 80 ? "ok" : k.respect_planning_pct >= 60 ? "warn" : "crit"} sub={`${k.of_a_temps_aujourdhui} à temps · ${k.of_en_retard_clotures_aujourdhui} en retard`} />
          <KpiTile testId="kpi-pieces" label="Production du jour" value={fmtNum(k.pieces_produites_aujourdhui)} unit={k.unite_production_jour || ""} />
          <KpiTile testId="kpi-objectif-pct" label="Objectif atteint" value={fmtPct(k.objectif_atteint_pct, 0)} tone={k.objectif_atteint_pct == null ? "none" : k.objectif_atteint_pct >= 95 ? "ok" : k.objectif_atteint_pct >= 80 ? "warn" : "crit"} />
          <KpiTile testId="kpi-charge" label="Charge des lignes" value={fmtPct(k.charge_lignes_pct, 0)} />
        </div>
      )}

      <Panel testId="of-table-panel" title={`Ordres de fabrication (${rows.length})`}
        actions={<>
          <div className="relative"><Search className="h-3 w-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" /><input data-testid="of-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="N°, produit, client, équipe…" className="h-6 w-48 bg-input border border-border rounded-sm pl-6 pr-2 text-[11px]" /></div>
          <select data-testid="of-filter-ligne" value={ligne} onChange={(e) => setLigne(e.target.value)} className="h-6 bg-input border border-border rounded-sm text-[11px] px-1.5"><option value="">Toutes lignes</option>{(lignes.data || []).map((l) => <option key={l.id} value={l.id}>{l.nom}</option>)}</select>
          <div className="flex gap-0.5">{STATUTS.map(([v, l]) => <button key={v} data-testid={`of-filter-statut-${v || "all"}`} onClick={() => setStatut(v)} className={cn("h-6 px-2 text-[11px] border rounded-sm", statut === v ? "bg-accent border-border" : "border-transparent text-muted-foreground hover:text-foreground")}>{l}</button>)}</div>
        </>}>
        {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} onRetry={q.refetch} />
          : <DataTable testId="of-table" columns={columns} rows={rows} rowKey={(r) => r.id} onRowClick={(r) => navigate(`/of/${r.id}`)} emptyTitle="Aucun OF" emptyHint="Créez un ordre de fabrication avec le bouton « Nouvel OF »." />}
      </Panel>

      <OfFormDialog open={!!form} of={form === "new" ? null : form} onClose={() => setForm(null)} />
    </div>
  );
}
